'use client';

import { useContext, useState } from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import { useToast } from '@mezzoforte/forge';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BiddingContext } from '@/context/BiddingContext';
import BidConfirmationModal from '@/features/Bidding/BidConfirmationModal';
import { BidSuggestionModal } from '@/features/Bidding/BidSuggestionModal';
import { SafelimitModal } from '@/features/Bidding/SafelimitModal';
import { useLoginModal } from '@/hooks/useLoginModal';
import { useSession } from '@/hooks/useSession';
import { AutobidAPIResponse, BidAPIResponse, BidFailureAPIResponse } from '@/types/ApiResponse';
import { createEcommerceItem, gtmService } from '@/util/gtm-service';
import { addVatToPrice } from '@/util/price';
import { useFavorites } from '@/hooks/useFavorites';
import { getManufacturer } from '@/util/metadata';
import { useApi } from '@/hooks/useApi';

type PlayToastParameters = Parameters<ReturnType<typeof useToast>['playToast']>;
const bidToastErrors: Record<string, PlayToastParameters> = {
  NO_ROLE: [
    'Käyttäjällä ei ole oikeuksia tehdä tarjouksia!',
    <>
      Pyydämme olemaan yhteydessä sähköpostiosoitteeseen
      <a href="mailto:asiakaspalvelu@huutokaupat.com">asiakaspalvelu@huutokaupat.com</a>, mikäli haluat liittää
      tunnukseesi oikeudet tehdä tarjouksia.
    </>,
    {},
  ],
  OWN_BID: ['Et voi jättää tarjousta omaan kohteeseesi!', null, { variant: 'warning' }],
};

export interface BidProps {
  minBidWithVat: number;
  safeLimit: number | null;
}

export default function useBid({ minBidWithVat: defaultMinBidWithVat, safeLimit }: BidProps) {
  const { playToast } = useToast();
  const { addFavorite } = useFavorites();
  const {
    entryId,
    companyName,
    categoryId,
    categoryName,
    parentCategoryId,
    parentCategoryName,
    title,
    vatPerc,
    location,
    metadata,
  } = useContext(BiddingContext);
  const { apiClient } = useApi();

  const [confirmedValue, setConfirmedValue] = useState(0);
  const [isAutobid, setIsAutobid] = useState(false);
  const [minBidWithVat, setMinBidWithVat] = useState(defaultMinBidWithVat);

  const [isBiddingModalOpen, setIsBiddingModalOpen] = useState(false);
  const [isBidSuggestionModalOpen, setIsBidSuggestionModalOpen] = useState(false);
  const [isSafelimitModalOpen, setIsSafelimitModalOpen] = useState(false);

  const openBidModal = (bidValue: number, isAutobid = false) => {
    gtmService.event('auction:open_bid_modal');

    if (!isAutobid) {
      if (bidValue < minBidWithVat) {
        setIsBidSuggestionModalOpen(true);
        return;
      }
      if (bidValue > (safeLimit ?? Infinity)) {
        setIsSafelimitModalOpen(true);
        return;
      }
    }

    setIsBiddingModalOpen(true);
  };

  const { LoginModal, openLoginModal } = useLoginModal(false, () => openBidModal(confirmedValue, isAutobid));
  const { currentUser } = useSession();
  const loggedIn = currentUser.data?.isAuthenticated ?? false;

  const queryClient = useQueryClient();

  const ecommerceItem = createEcommerceItem({
    id: String(entryId),
    name: title,
    company: companyName,
    brand: getManufacturer(metadata),
    category: { id: categoryId, name: categoryName },
    parentCategory:
      parentCategoryId !== null && parentCategoryName !== null
        ? { id: parentCategoryId, name: parentCategoryName }
        : undefined,
    location,
    priceWithoutShippingOrTax: confirmedValue,
  });

  const bidMutation = useMutation<
    BidAPIResponse,
    AxiosError<BidFailureAPIResponse> | AxiosResponse<BidFailureAPIResponse>,
    { id: string | number; amount: string | number }
  >({
    mutationFn: ({ id, amount }) =>
      apiClient.post<BidAPIResponse>('/api/net-auction-bid/bid', { id, bid_amount: amount }).then((response) => {
        if (response.data.errorType) return Promise.reject(response);
        return Promise.resolve(response.data);
      }),
    onSuccess: () => {
      // GTM events
      gtmService.customEcommerce.bid(ecommerceItem);

      // Add entry to favorites
      addFavorite.mutate(entryId);
    },
    onError: (error) => {
      const data = 'data' in error ? error?.data : error.response?.data;

      if (error.status === 403) {
        playToast('Istuntosi on vanhentunut. Kirjaudu uudestaan sisään tarjotaksesi kohteeseen.', null, {
          variant: 'warning',
        });

        openLoginModal();
        return;
      }

      if (data) {
        const { errorType, errorMessage } = data;

        if (errorType === 'UNAUTHORIZED') {
          openLoginModal();
          return;
        }
        if (errorType === 'BID_TOO_LOW') {
          const { highestBid, minimumRaise } = data;
          setMinBidWithVat(addVatToPrice({ amount: highestBid + Number(minimumRaise) }, vatPerc).amount);
          setIsBidSuggestionModalOpen(true);
          return;
        }
        if (errorType in bidToastErrors) {
          playToast(...bidToastErrors[errorType]);
          return;
        }

        if (safeLimit !== null && confirmedValue > safeLimit) {
          gtmService.event('auction:bid_too_high', { amount: confirmedValue });
          setIsSafelimitModalOpen(true);
          return;
        }

        if (errorMessage) {
          playToast(errorMessage, null, { variant: 'warning' });
          return;
        }
      }

      playToast('Jokin meni pieleen! Tarjoaminen epäonnistui.', null, { variant: 'danger' });
    },
  });

  const botMutation = useMutation<
    AutobidAPIResponse,
    AxiosError<BidFailureAPIResponse> | AxiosResponse<BidFailureAPIResponse>,
    { id: string | number; amount: string | number }
  >({
    mutationFn: ({ id, amount }) =>
      apiClient.post<AutobidAPIResponse>('/api/net-auction-bid/autobid', { id, bid_max: amount }).then((response) => {
        if (response.data.errorType) return Promise.reject(response);
        return Promise.resolve(response.data);
      }),
    onSuccess: () => {
      gtmService.customEcommerce.activateBiddingBot(ecommerceItem);
      addFavorite.mutate(entryId);
    },
    onError: (error) => {
      const data = 'data' in error ? error?.data : error.response?.data;
      if (error?.status === 403) {
        playToast('Istuntosi on vanhentunut. Kirjaudu uudestaan sisään aktivoidaksesi korotusautomaatin.', null, {
          variant: 'warning',
        });

        openLoginModal();
        return;
      }

      if (data) {
        const { errorType, errorMessage } = data;
        if (errorType === 'UNAUTHORIZED') {
          openLoginModal();
          return;
        }
        if (errorType in bidToastErrors) {
          playToast(...bidToastErrors[errorType]);
          return;
        }
        if (errorMessage) {
          playToast(errorMessage, null, { variant: 'danger' });
          return;
        }
      }

      playToast('Jotain meni pieleen! Korotusautomaatin aktivointi epäonnistui.', null, { variant: 'danger' });
    },
  });

  const confirmBid = (bidValue: number, isAutobid = false) => {
    setConfirmedValue(bidValue);
    setIsAutobid(isAutobid);

    if (!loggedIn) {
      openLoginModal();
      return;
    }

    openBidModal(bidValue, isAutobid);
  };

  const submitBid = () => {
    const onSettled = () => {
      void queryClient.invalidateQueries({ queryKey: ['currentUser', 'entries', entryId] });
    };
    if (isAutobid) {
      botMutation.mutate({ id: entryId, amount: confirmedValue }, { onSettled });
    } else {
      bidMutation.mutate({ id: entryId, amount: confirmedValue }, { onSettled });
    }
  };

  function BidModals() {
    return (
      <>
        <BidConfirmationModal
          isOpen={isBiddingModalOpen}
          onDismiss={() => setIsBiddingModalOpen(false)}
          submitBid={submitBid}
          entryId={entryId}
          entryTitle={title}
          bidValue={confirmedValue}
          isAutobid={isAutobid}
        />
        <BidSuggestionModal
          onBid={() => {
            setIsBidSuggestionModalOpen(false);
            setIsBiddingModalOpen(true);
            confirmBid(minBidWithVat);
          }}
          confirmedBid={confirmedValue}
          minBid={minBidWithVat}
          isOpen={isBidSuggestionModalOpen}
          onClose={() => setIsBidSuggestionModalOpen(false)}
        />
        <SafelimitModal
          safeLimit={safeLimit}
          bidValue={confirmedValue}
          isOpen={isSafelimitModalOpen}
          onClose={() => setIsSafelimitModalOpen(false)}
        />
        <LoginModal />
      </>
    );
  }

  return {
    confirmBid,
    BidModals,
  };
}
